import { EXPENSE_CATEGORY_OPTIONS, EXPENSE_CATEGORY_VALUES } from './expenseCategories';
import { expT, expenseLang } from '../utils/expenseI18n';
import { isBrickFactoryUser } from '../utils/businessType';
import { normalizeRole } from '../utils/userLocale';

export function factoryExpenseCategoryOptions(lang) {
  return [
    {
      value: EXPENSE_CATEGORY_VALUES.STAFF,
      label: expT(lang, 'exp.cat.staff.label'),
      hint: expT(lang, 'exp.cat.staff.hint'),
    },
    {
      value: EXPENSE_CATEGORY_VALUES.OFFICE,
      label: expT(lang, 'exp.cat.office.label'),
      hint: expT(lang, 'exp.cat.office.hint'),
    },
    {
      value: EXPENSE_CATEGORY_VALUES.BOSS,
      label: expT(lang, 'exp.cat.boss.label'),
      hint: expT(lang, 'exp.cat.boss.hint'),
    },
  ];
}

export function factoryExpenseSummaryKeys(lang, user) {
  const owner = normalizeRole(user?.role) === 'owner';
  const keys = [
    {
      key: 'staff',
      label: expT(lang, 'exp.cat.staff.short'),
      shortLabel: expT(lang, 'exp.cat.staff.short'),
      pageKey: 'expenses-employee-history',
      path: '/employee-expenses',
      addCategory: EXPENSE_CATEGORY_VALUES.STAFF,
    },
    {
      key: 'office',
      label: expT(lang, 'exp.cat.office.label'),
      shortLabel: expT(lang, 'exp.cat.office.short'),
      pageKey: 'expenses-office-history',
      path: '/office-expenses',
      addCategory: EXPENSE_CATEGORY_VALUES.OFFICE,
    },
    {
      key: 'boss',
      label: expT(lang, 'exp.cat.boss.short'),
      shortLabel: expT(lang, 'exp.cat.boss.short'),
      pageKey: 'expenses-boss-history',
      path: '/boss-expenses',
      addCategory: EXPENSE_CATEGORY_VALUES.BOSS,
      ownerOnly: true,
    },
  ];
  return keys.filter((k) => !k.ownerOnly || owner);
}

export function expenseCategoryOptionsForUser(user, lang = expenseLang(user)) {
  if (!isBrickFactoryUser(user)) {
    return EXPENSE_CATEGORY_OPTIONS.map(({ value }) => ({
      value,
      label: expenseCategoryLabelForUser(user, value, lang),
    }));
  }
  return factoryExpenseCategoryOptions(lang);
}

export function expenseCategoryLabelForUser(user, value, lang = expenseLang(user)) {
  const v = String(value || '').toLowerCase();
  if (!isBrickFactoryUser(user)) {
    if (v === EXPENSE_CATEGORY_VALUES.BOSS) return expT(lang, 'exp.cat.boss.retail');
    if (v === EXPENSE_CATEGORY_VALUES.STAFF) return expT(lang, 'exp.cat.staff.retail');
    if (v === EXPENSE_CATEGORY_VALUES.OFFICE) return expT(lang, 'exp.cat.office.retail');
    return value || '—';
  }
  if (v === EXPENSE_CATEGORY_VALUES.STAFF) return expT(lang, 'exp.cat.staff.short');
  if (v === EXPENSE_CATEGORY_VALUES.OFFICE) return expT(lang, 'exp.cat.office.label');
  if (v === EXPENSE_CATEGORY_VALUES.BOSS) return expT(lang, 'exp.cat.boss.short');
  return value || '—';
}

export function expenseSummaryKeysForUser(user, lang = expenseLang(user)) {
  if (!isBrickFactoryUser(user)) {
    return [
      { key: 'boss', label: expT(lang, 'exp.cat.boss.retail'), pageKey: 'expenses-boss-history', path: '/boss-expenses' },
      { key: 'staff', label: expT(lang, 'exp.cat.staff.retail'), pageKey: 'expenses-employee-history', path: '/employee-expenses' },
      { key: 'office', label: expT(lang, 'exp.cat.office.retail'), pageKey: 'expenses-office-history', path: '/office-expenses' },
    ];
  }
  return factoryExpenseSummaryKeys(lang, user);
}

export function factoryExpenseListMeta(category, user, lang = expenseLang(user)) {
  if (!isBrickFactoryUser(user)) return null;
  if (category === EXPENSE_CATEGORY_VALUES.STAFF) {
    return { title: expT(lang, 'exp.list.staff.title'), lede: expT(lang, 'exp.list.staff.lede') };
  }
  if (category === EXPENSE_CATEGORY_VALUES.OFFICE) {
    return { title: expT(lang, 'exp.list.office.title'), lede: expT(lang, 'exp.list.office.lede') };
  }
  if (category === EXPENSE_CATEGORY_VALUES.BOSS) {
    return { title: expT(lang, 'exp.list.boss.title'), lede: expT(lang, 'exp.list.boss.lede') };
  }
  return null;
}

/** @deprecated use factoryExpenseCategoryOptions(lang) */
export const FACTORY_EXPENSE_CATEGORY_OPTIONS = factoryExpenseCategoryOptions('sw');

/** @deprecated use factoryExpenseSummaryKeys(lang, user) */
export const FACTORY_EXPENSE_SUMMARY_KEYS = factoryExpenseSummaryKeys('sw', { role: 'owner' });

export function factoryOperatingExpenseTotal(buckets) {
  if (!buckets) return 0;
  return (Number(buckets.staff) || 0) + (Number(buckets.office) || 0);
}

export function factoryExpenseTotalAll(buckets) {
  if (!buckets) return 0;
  return (Number(buckets.boss) || 0) + factoryOperatingExpenseTotal(buckets);
}
